import {
  Grid,
  Paper,
  lighten,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import React, { useContext, useState } from "react";

import Button from "../Button";
import ConfirmDialog from "../ConfirmDialog";
import { GetCancerScreeningResultWithQueryModel } from "../../models/getCancerScreeningResultModel";
import { LangContext } from "../../context/LangContext";
import ScreeningRequestDetail from "./ScreeningRequestDetail";
import { ScreeningStatus } from "../../models/getCancerScreeningsModel";
import TestResults from "./TestResults";
import { UserContext } from "../../context/UserContext";
import translations from "../../assets/json/translations.json";
import { useHistory } from "react-router-dom";

interface IProps {
  screeningRequest: GetCancerScreeningResultWithQueryModel;
  reopenScreeningRequestInParent: (
    id: number,
    userSsn: string
  ) => Promise<void>;
  sendToSkrinInParent: (id: number, userSsn: string) => Promise<void>;
  resetScreeningRequestInParent: (id: number, userSsn: string) => Promise<void>;
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(5),
  },
  topItems: {
    marginBottom: theme.spacing(4),
    [theme.breakpoints.only("md")]: {
      marginBottom: theme.spacing(2),
    },
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(2),
    },
  },
  titleDivider: {
    display: "inline-block",
    margin: theme.spacing(0, 1),
  },
  titleSerialNumber: {
    display: "inline-block",
    marginRight: theme.spacing(1),
  },
  otherDetails: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
  },
  descriptionTitle: {
    fontWeight: "bold",
    marginRight: theme.spacing(1),
    display: "inline-block",
  },
  descriptionDetails: {
    color: theme.palette.text.secondary,
    display: "inline-block",
  },
  resultsWrapper: {
    backgroundColor: lighten(theme.palette.secondary.light, 0.8),
    padding: theme.spacing(2),
  },
}));

const ScreeningRequestOverviewDetails: React.FC<IProps> = ({
  screeningRequest,
  reopenScreeningRequestInParent,
  sendToSkrinInParent,
  resetScreeningRequestInParent,
}) => {
  const classes = useStyles();
  const [lang] = useContext(LangContext);
  const history = useHistory();
  const theme = useTheme();

  const [openSendToSkrinDialog, setOpenSendToSkrinDialog] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [openReopenDialog, setOpenReopenDialog] = useState(false);

  const [user] = useContext(UserContext);
  const xsDown = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <Paper elevation={2} className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ScreeningRequestDetail
            screeningRequest={screeningRequest.cancerScreening}
          />
        </Grid>
        <Grid item xs={12}>
          {screeningRequest.cancerScreening.screeningStatus ===
            ScreeningStatus.Completed && (
            <TestResults screeningRequest={screeningRequest} />
          )}
        </Grid>

        <Grid item xs={12}>
          {renderButtons()}
        </Grid>
      </Grid>
      {renderDialogs()}
    </Paper>
  );

  function renderButtons() {
    return (
      <Grid
        container
        spacing={2}
        justify="flex-end"
        direction="row"
        alignItems="stretch"
        wrap={xsDown ? "wrap" : "nowrap"}
      >
        {screeningRequest.cancerScreening.screeningStatus ===
          ScreeningStatus.Completed && (
          <Grid item>
            <Button
              onClick={() => setOpenResetDialog(true)}
              variant="contained"
            >
              {translations.resetOrWithdrawScreening[lang]}
            </Button>
          </Grid>
        )}
        {screeningRequest.cancerScreening.screeningStatus ===
          ScreeningStatus.Created && (
          <Grid item>
            <Button
              onClick={() => setOpenSendToSkrinDialog(true)}
              variant="contained"
            >
              {translations.sendToSkrin[lang]}
            </Button>
          </Grid>
        )}
        {(screeningRequest.cancerScreening.screeningStatus ===
          ScreeningStatus.Canceled ||
          screeningRequest.cancerScreening.screeningStatus ===
            ScreeningStatus.SentToSkrin) && (
          <Grid item>
            <Button
              onClick={() => setOpenReopenDialog(true)}
              variant="contained"
            >
              {translations.reopen[lang]}
            </Button>
          </Grid>
        )}
        <Grid item>
          <Button
            onClick={() => history.push("/screening-requests-overview")}
            variant="outlined"
          >
            {translations.back[lang]}
          </Button>
        </Grid>
      </Grid>
    );
  }

  function renderDialogs() {
    return (
      <>
        <ConfirmDialog
          open={openSendToSkrinDialog}
          title={translations.sendToSkrin[lang]}
          onCancel={() => setOpenSendToSkrinDialog(false)}
          onConfirm={() =>
            screeningRequest.cancerScreening.id &&
            user.ssn &&
            sendToSkrin(screeningRequest.cancerScreening.id, user.ssn)
          }
          confirmText={translations.send[lang]}
          text={translations.sendToSkrinDialogContent[lang]}
        ></ConfirmDialog>

        <ConfirmDialog
          open={openReopenDialog}
          title={translations.reopen[lang]}
          onCancel={() => setOpenReopenDialog(false)}
          onConfirm={() =>
            screeningRequest.cancerScreening.id &&
            user.ssn &&
            reopenScreeningRequest(
              screeningRequest.cancerScreening.id,
              user.ssn
            )
          }
          confirmText={translations.reopen[lang]}
          text={translations.reopenDialogContent[lang]}
        ></ConfirmDialog>
        <ConfirmDialog
          open={openResetDialog}
          title={translations.resetOrWithdrawScreening[lang]}
          onCancel={() => setOpenResetDialog(false)}
          onConfirm={() =>
            screeningRequest.cancerScreening.id &&
            user.ssn &&
            resetScreeningRequest(
              screeningRequest.cancerScreening.id,
              user.ssn
            )
          }
          confirmText={translations.resetOrWithdrawScreening[lang]}
          text={translations.resetDialogContent[lang]}
        ></ConfirmDialog>
      </>
    );
  }

  async function reopenScreeningRequest(id: number, userSsn: string) {
    await reopenScreeningRequestInParent(id, userSsn);
    setOpenReopenDialog(false);
  }

  async function sendToSkrin(id: number, userSsn: string) {
    await sendToSkrinInParent(id, userSsn);

    setOpenSendToSkrinDialog(false);
  }
  async function resetScreeningRequest(id: number, userSsn: string) {
    await resetScreeningRequestInParent(id, userSsn);

    setOpenResetDialog(false);
  }
};

export default ScreeningRequestOverviewDetails;
